import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { blogPosts } from '../data/blogData';
import type { BlogPostSummary } from '../data/blogPosts.generated';
import { getBlogCategory } from '../data/blogSeo';

const deriveCategory = (post: BlogPostSummary): string => {
  return getBlogCategory(post.slug);
};

// Görüntülenme sayısı yayın tarihine göre skala edilir:
// - Yeni yazı (< 1 gün): 3-14
// - İlk hafta: 15-80
// - İlk ay: 60-300
// - İlk 3 ay: 260-680
// - İlk yıl: 600-1350
// - 1+ yıl: 1100-1500 (cap)
// Slug hash'iyle deterministik varyans; ileride gerçek analitik bağlanırsa kaldırılır.
const fakeViewCount = (slug: string, publishedAt: string): number => {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) | 0;
  }
  const positive = Math.abs(hash);
  const days = Math.max(
    0,
    (Date.now() - new Date(publishedAt).getTime()) / 86400000,
  );

  let base: number;
  let variance: number;
  if (days < 1) {
    base = 3;
    variance = positive % 12;
  } else if (days < 7) {
    base = 15 + Math.floor(days * 6);
    variance = positive % 25;
  } else if (days < 30) {
    base = 60 + Math.floor((days - 7) * 8);
    variance = positive % 60;
  } else if (days < 90) {
    base = 260 + Math.floor((days - 30) * 5);
    variance = positive % 120;
  } else if (days < 365) {
    base = 600 + Math.floor((days - 90) * 2);
    variance = positive % 200;
  } else {
    base = 1100 + Math.floor((days - 365) * 0.5);
    variance = positive % 300;
  }

  return Math.min(1500, base + variance);
};

const formatViews = (n: number) => n.toLocaleString('tr-TR');

const ALL_TAB = 'Tüm Yazılar';
const POSTS_PER_PAGE = 6;

const categoryOrder = [
  ALL_TAB,
  'Almanya Blue Card',
  "Almanya'da Eğitim",
  "Almanya'da Yaşam",
  "Ayşegül'den",
  "Almanya'ya Göç",
];

const BlogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCategory = searchParams.get('kategori');
  // Tüm yazılara türetilmiş kategori bilgisini ekliyoruz.
  // Gelecek tarihli yazıları listede gizle (planlı yayın için).
  const postsWithCategory = useMemo(
    () => {
      const now = Date.now();
      return blogPosts
        .filter((p) => new Date(p.publishedAt).getTime() <= now)
        .map((p) => ({ ...p, derivedCategory: deriveCategory(p) }));
    },
    [],
  );

  const [activeCategory, setActiveCategory] = useState<string>(
    requestedCategory && categoryOrder.includes(requestedCategory) ? requestedCategory : ALL_TAB,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setActiveCategory(
      requestedCategory && categoryOrder.includes(requestedCategory) ? requestedCategory : ALL_TAB,
    );
    setPage(1);
  }, [requestedCategory]);

  const filteredPosts = useMemo(() => {
    const categoryPosts = activeCategory === ALL_TAB
      ? postsWithCategory
      : postsWithCategory.filter((p) => p.derivedCategory === activeCategory);
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase('tr-TR');

    if (!normalizedQuery) return categoryPosts;

    return categoryPosts.filter((post) => {
      const searchableText = `${post.title} ${post.excerpt} ${post.derivedCategory}`
        .toLocaleLowerCase('tr-TR');
      return searchableText.includes(normalizedQuery);
    });
  }, [postsWithCategory, activeCategory, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * POSTS_PER_PAGE;
  const visiblePosts = filteredPosts.slice(pageStart, pageStart + POSTS_PER_PAGE);

  const handleCategoryClick = (cat: string) => {
    setActiveCategory(cat);
    setPage(1);
    if (cat === ALL_TAB) {
      setSearchParams({});
    } else {
      setSearchParams({ kategori: cat });
    }
  };

  const goTo = (target: number) => {
    setPage(Math.min(Math.max(1, target), totalPages));
    // Sayfa değişince yukarı kaydır — yeni yazıların başına gidilsin
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="blog-page">
      {/* Hero — açık yeşil */}
      <section className="blog-hero">
        <div className="container">
          <h1 className="blog-hero-title">Göç Notları</h1>
          <p className="blog-hero-text">
            Almanya'ya göç hazırlığından yerleşim sonrasındaki günlük yaşama kadar edindiğim
            deneyimleri Göç Notları'nda paylaşıyorum. Burada süreci daha iyi anlamanıza yardımcı
            olacak kişisel deneyimler, pratik bilgiler ve faydalı ipuçları bulabilirsiniz.
          </p>
        </div>
      </section>

      {/* Kategori tabları + kartlar */}
      <section className="blog-content-section">
        <div className="container">
          <div className="blog-search">
            <svg className="blog-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setPage(1);
              }}
              className="blog-search-input"
              placeholder="Yazılarda ara: Anmeldung, vize, Kita..."
              aria-label="Göç Notları yazılarında ara"
            />
          </div>

          <div className="blog-categories-row">
            {categoryOrder.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`blog-category-tab${activeCategory === cat ? ' active' : ''}`}
                onClick={() => handleCategoryClick(cat)}
                aria-pressed={activeCategory === cat}
              >
                {cat}
              </button>
            ))}
          </div>

          {visiblePosts.length > 0 ? (
            <div className="blog-grid">
              {visiblePosts.map((post) => (
              <Link to={post.slug} key={post.slug} className="blog-card">
                <div className="blog-card-image">
                  {post.image ? (
                    <img src={post.image} alt={post.title} loading="lazy" />
                  ) : (
                    <div className="blog-card-placeholder">
                      <span>📝</span>
                    </div>
                  )}
                </div>
                <div className="blog-card-content">
                  <div className="blog-card-meta">
                    <div className="blog-card-meta-line">
                      <span className="blog-card-date">{post.date}</span>
                      <span className="blog-card-meta-dot">·</span>
                      <span className="blog-card-reading-time">{post.readingTime}</span>
                    </div>
                    <span
                      className="blog-card-views"
                      aria-label={`${formatViews(fakeViewCount(post.slug, post.publishedAt))} görüntülenme`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {formatViews(fakeViewCount(post.slug, post.publishedAt))}
                    </span>
                  </div>
                  <span className="blog-card-category-tag">{post.derivedCategory}</span>
                  <h2 className="blog-card-title">{post.title}</h2>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <span className="blog-card-read-more">Devamını Oku →</span>
                </div>
              </Link>
              ))}
            </div>
          ) : (
            <div className="blog-search-empty" role="status">
              <h2>Aramanızla eşleşen bir yazı bulunamadı.</h2>
              <p>Farklı bir kelime deneyebilir veya başka bir kategori seçebilirsiniz.</p>
            </div>
          )}

          {totalPages > 1 && (
            <nav className="blog-pagination" aria-label="Sayfalama">
              <button
                type="button"
                className="blog-page-btn blog-page-btn-edge"
                onClick={() => goTo(1)}
                disabled={safePage === 1}
                aria-label="İlk sayfa"
              >
                «
              </button>
              <button
                type="button"
                className="blog-page-btn"
                onClick={() => goTo(safePage - 1)}
                disabled={safePage === 1}
                aria-label="Önceki sayfa"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`blog-page-btn blog-page-btn-num${n === safePage ? ' active' : ''}`}
                  onClick={() => goTo(n)}
                  aria-current={n === safePage ? 'page' : undefined}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                className="blog-page-btn"
                onClick={() => goTo(safePage + 1)}
                disabled={safePage === totalPages}
                aria-label="Sonraki sayfa"
              >
                ›
              </button>
              <button
                type="button"
                className="blog-page-btn blog-page-btn-edge"
                onClick={() => goTo(totalPages)}
                disabled={safePage === totalPages}
                aria-label="Son sayfa"
              >
                »
              </button>
            </nav>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
